import { Button } from "@package/subframe/components/Button";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { FeatherTrash2 } from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { useState } from "react";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useDeleteTaskInstanceCommandControllerDelete } from "@/generated/compliance-tasks/compliance-tasks";
import type { ComplianceTaskInstanceResponseDto } from "@/generated/model";
import {
  extractApiErrorMessage,
  invalidateComplianceQueries,
} from "@/lib/compliance/invalidate-compliance-queries";

interface DeleteInstanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instance: ComplianceTaskInstanceResponseDto;
  /**
   * True when this is the definition's only open instance — deleting it ends
   * the series (nothing regenerates until the task is completed again), so
   * the dialog warns more strongly.
   */
  isOnlyOpenInstance?: boolean;
}

export function DeleteInstanceDialog({
  open,
  onOpenChange,
  instance,
  isOnlyOpenInstance = false,
}: DeleteInstanceDialogProps) {
  const queryClient = useQueryClient();
  const deleteInstance = useDeleteTaskInstanceCommandControllerDelete();
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await deleteInstance.mutateAsync({ instanceId: instance.id });
      toast.success("Task instance deleted");
      await invalidateComplianceQueries(queryClient);
      onOpenChange(false);
    } catch (error: unknown) {
      toast.error(
        extractApiErrorMessage(error, "Failed to delete the task instance"),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} className="max-w-[480px] p-0">
        <DialogTitle className="sr-only">
          Delete compliance task instance
        </DialogTitle>
        <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-white">
          <div className="flex max-w-[448px] flex-col items-center gap-6 p-4">
            <div className="flex flex-col items-center gap-6">
              <IconWithLogoBackground
                variant="error"
                size="x-large"
                icon={<FeatherTrash2 />}
              />
              <div className="flex flex-col items-center gap-2">
                <span className="w-full text-center text-heading-2 font-heading-2 text-default-font">
                  Delete this instance?
                </span>
                <span className="text-center text-body font-body text-subtext-color">
                  The instance due{" "}
                  <strong>
                    {format(new Date(instance.dueDate), "d MMM yyyy")}
                  </strong>{" "}
                  and its uploaded evidence will be permanently removed. Use
                  this to remove a duplicate or mistaken entry — to fix a wrong
                  due date, reschedule instead.
                </span>
                {isOnlyOpenInstance && (
                  <span className="text-center text-body-bold font-body-bold text-error-700">
                    This is the task&apos;s only upcoming instance. Deleting it
                    ends the series — nothing new will be scheduled until the
                    task is completed again.
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex w-full flex-col items-center gap-3 bg-white px-4 py-4">
            <Button
              className="h-14 w-80 flex-none"
              variant="neutral-secondary"
              size="extra-large"
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              className="h-14 w-80 flex-none"
              variant="destructive-primary"
              size="extra-large"
              onClick={handleConfirm}
              disabled={busy}
            >
              {busy ? "Deleting..." : "Yes, delete"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
